
import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { Brand } from "@/hooks/useBrandManager";

interface BrandSelectionDialogProps {
  open: boolean;
  brands: Brand[];
  onBrandSelect: (brandId: string) => void;
  onCreateBrand?: () => void;
}

export const BrandSelectionDialog = ({ 
  open, 
  brands, 
  onBrandSelect,
  onCreateBrand 
}: BrandSelectionDialogProps) => {
  // Group brands by ownership vs participation
  const ownedBrands = brands.filter(brand => brand.role === 'owner');
  const participatedBrands = brands.filter(brand => brand.role !== 'owner');

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select a Brand</DialogTitle>
          <DialogDescription>
            Choose a brand to continue working with.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {/* Brands You Own Section */}
          {ownedBrands.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2">
                Brands You Own
              </h3>
              <div className="space-y-1">
                {ownedBrands.map((brand) => (
                  <Button
                    key={brand.id}
                    variant="ghost"
                    className="w-full justify-start text-left h-auto p-3 hover:bg-gray-50"
                    onClick={() => onBrandSelect(brand.id)}
                  >
                    <div className="flex items-center w-full">
                      <div className="w-2 h-2 bg-gray-300 rounded-full mr-3"></div>
                      <span className="font-medium text-gray-700 text-sm">
                        {brand.name}
                      </span>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Brands You Participate Section */}
          {participatedBrands.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2">
                Brands You Participate
              </h3>
              <div className="space-y-1">
                {participatedBrands.map((brand) => (
                  <Button
                    key={brand.id}
                    variant="ghost"
                    className="w-full justify-start text-left h-auto p-3 hover:bg-gray-50"
                    onClick={() => onBrandSelect(brand.id)}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-gray-300 rounded-full mr-3"></div>
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-700 text-sm">
                            {brand.name}
                          </span>
                          <span className="text-xs text-gray-500 capitalize">
                            {brand.role}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* New Brand Button */}
          <div className="pt-2 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start text-left h-auto p-3 hover:bg-gray-50"
              onClick={onCreateBrand}
            >
              <div className="flex items-center">
                <Plus className="h-4 w-4 text-gray-400 mr-3" />
                <span className="font-medium text-gray-700 text-sm">
                  New Brand
                </span>
              </div>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
